/**
 * Reaction helpers: derive aggregated m.reaction annotations from a room's
 * live timeline, and send/toggle a reaction. Kept framework-free so the hook
 * and components stay thin.
 */
import { EventType, RelationType, type MatrixClient, type Room } from "matrix-js-sdk";

export interface ReactionSummary {
  key: string; // the emoji
  count: number;
  mine: boolean;
  myReactionId: string | null; // this user's reaction event id (for toggle-off)
}

/** targetEventId -> list of reaction summaries (sorted by count desc). */
export type ReactionsByEvent = Record<string, ReactionSummary[]>;

interface Relation {
  rel_type?: string;
  event_id?: string;
  key?: string;
}

/**
 * Aggregate all non-redacted m.reaction annotation events currently loaded in
 * the room's live timeline, keyed by the message they target.
 */
export function getRoomReactions(client: MatrixClient, room: Room): ReactionsByEvent {
  const myId = client.getUserId();
  const events = room.getLiveTimeline().getEvents();

  // Local accumulator (freshly allocated — no external mutation).
  const acc: Record<string, Record<string, ReactionSummary>> = {};

  for (const ev of events) {
    if (ev.getType() !== EventType.Reaction || ev.isRedacted()) continue;
    const relates = ev.getContent()["m.relates_to"] as Relation | undefined;
    if (!relates || relates.rel_type !== RelationType.Annotation) continue;
    const targetId = relates.event_id;
    const key = relates.key;
    if (!targetId || !key) continue;

    const byKey = acc[targetId] ?? (acc[targetId] = {});
    const entry =
      byKey[key] ?? (byKey[key] = { key, count: 0, mine: false, myReactionId: null });
    entry.count += 1;
    if (ev.getSender() === myId) {
      entry.mine = true;
      entry.myReactionId = ev.getId() ?? null;
    }
  }

  const out: ReactionsByEvent = {};
  for (const targetId of Object.keys(acc)) {
    out[targetId] = Object.values(acc[targetId]).sort(
      (a, b) => b.count - a.count || a.key.localeCompare(b.key),
    );
  }
  return out;
}

/**
 * Toggle a reaction on a target message: redact my existing one, or send a new
 * annotation. Errors bubble to the caller (which treats them as non-fatal).
 */
export async function toggleReaction(
  client: MatrixClient,
  roomId: string,
  targetEventId: string,
  key: string,
  existing: ReactionSummary | undefined,
): Promise<void> {
  if (existing?.mine && existing.myReactionId) {
    await client.redactEvent(roomId, existing.myReactionId);
    return;
  }
  await client.sendEvent(roomId, EventType.Reaction, {
    "m.relates_to": { rel_type: RelationType.Annotation, event_id: targetEventId, key },
  } as never);
}
